import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, Star, Crown, Loader2, Cake, PlayCircle, Heart, Clock, Gift, Ticket } from "lucide-react";
import { trackInitiateCheckout } from "../lib/metaPixel";

const VALUE_ITEMS = [
  { icon: Cake, title: "One Monthly Open Studio Visit", note: "A $39 value.", desc: "Drop in, grab a workstation, and create — one free visit every month, cake kit included." },
  { icon: PlayCircle, title: "Unlimited Access to The Cake Society Vault", note: "", desc: "A growing library of decorating tutorials, techniques, and creative projects." },
  { icon: Heart, title: "15% Member Savings", note: "", desc: "On classes, camps, DIY kits, merchandise, and retail." },
  { icon: Clock, title: "Early Access", note: "", desc: "Register before the general public for classes and camps." },
  { icon: Gift, title: "Exclusive Member Perks", note: "", desc: "Member-only surprises, special offers, and exclusive experiences." },
];

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  const loadMine = async () => {
    if (!user || user === false) { setMine(null); return; }
    try { const { data } = await api.get("/membership/me"); setMine(data); }
    catch { setMine(null); }
  };

  const doRedeem = async (rawCode) => {
    const c = (rawCode || "").trim().toUpperCase();
    if (!c) { toast.error("Enter a membership code"); return; }
    setCodeBusy(true);
    try {
      const { data: v } = await api.post("/membership/validate-code", { code: c });
      if (!user || user === false) {
        localStorage.setItem("cs_pending_membership_code", c);
        toast.success(`Code valid — ${v.months} free month${v.months > 1 ? "s" : ""}! Sign in or create an account to claim it.`);
        nav("/login?next=/membership");
        return;
      }
      const { data } = await api.post("/membership/redeem-code", { code: c });
      localStorage.removeItem("cs_pending_membership_code");
      setCode("");
      toast.success(data.mode === "stripe_skip"
        ? `Done! Your next ${data.months} month${data.months > 1 ? "s are" : " is"} free — no charge.`
        : `Welcome! You now have ${data.months} free month${data.months > 1 ? "s" : ""} of membership.`);
      await loadMine();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "That code isn't valid.");
      localStorage.removeItem("cs_pending_membership_code");
    } finally { setCodeBusy(false); }
  };

  useEffect(() => {
    api.get("/membership/info").then(({ data }) => setInfo(data)).catch(() => {});
    try {
      const urlCode = new URLSearchParams(window.location.search).get("code");
      if (urlCode) { setCode(urlCode.toUpperCase()); localStorage.setItem("cs_pending_membership_code", urlCode.trim().toUpperCase()); }
    } catch { /* ignore */ }
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMine(); }, [user]);

  // After login, auto-redeem a code the visitor entered while logged out
  const autoRedeemedRef = useRef(false);
  useEffect(() => {
    if (!user || user === false || autoRedeemedRef.current) return;
    let pending;
    try { pending = localStorage.getItem("cs_pending_membership_code"); } catch { pending = null; }
    if (pending) {
      autoRedeemedRef.current = true;
      try { localStorage.removeItem("cs_pending_membership_code"); } catch { /* ignore */ }
      doRedeem(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const subscribe = async () => {
    if (!user || user === false) { nav("/login?next=/membership"); return; }
    setBusy(true);
    trackInitiateCheckout({ value: price, name: "Cake Society Membership" });
    try {
      const { data } = await api.post("/membership/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel your membership at the end of the current billing period?")) return;
    setBusy(true);
    try {
      await api.post("/membership/cancel");
      toast.success("Membership will end at the close of your billing period.");
      await loadMine();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const perks = info?.perks || [];
  const price = info?.price ?? 29;

  return (
    <div data-testid="membership-page" className="pt-32 pb-24 fade-in bg-[#F2E9D8]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold">Cake Society Membership</p>
        <h1 className="font-serif font-black text-6xl sm:text-7xl text-[#121212] leading-[0.95] mt-4">
          Become a <span className="lime-swipe italic">member.</span>
        </h1>
        <p className="mt-6 text-lg max-w-2xl text-[#121212]/80">
          One simple monthly membership — and your standing invitation to create, learn, and belong.
          Free studio time, the full Vault, member savings, and first dibs on everything we make.
        </p>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 items-start">
          {/* Perks list */}
          <div className="bg-white border-2 border-[#121212] p-10">
            <div className="flex items-center gap-3">
              <Crown className="text-[#121212]" size={28} />
              <h2 className="font-serif font-black text-3xl text-[#121212]">What you get</h2>
            </div>
            <ul className="mt-8 space-y-5">
              {perks.map((p, i) => (
                <li key={i} data-testid={`perk-${i}`} className="flex items-start gap-3">
                  <span className="mt-1 shrink-0 w-6 h-6 bg-[#C6FF33] border-2 border-[#121212] flex items-center justify-center">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span className="text-[#121212] text-lg leading-snug">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Price / subscribe card */}
          <div className="bg-[#121212] text-[#F2E9D8] p-10 relative">
            <Star className="absolute top-6 right-6 text-[#C6FF33]" size={36} fill="#C6FF33" />
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Monthly Membership</p>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-serif font-black text-7xl">${price}</span>
              <span className="text-[#F2E9D8]/70 mb-3 text-lg">/ month</span>
            </div>
            <p className="mt-2 text-[#F2E9D8]/70 text-sm">Cancel anytime. Perks renew every billing cycle.</p>

            {mine?.active ? (
              <div className="mt-8 space-y-4" data-testid="member-status-box">
                <div className="bg-[#C6FF33] text-[#121212] p-4 border-2 border-[#C6FF33]">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold">You're a member 🎉</p>
                  <p className="text-sm mt-1 font-medium">
                    Free Open Studio visits left this month: {mine.free_visits_remaining}
                  </p>
                  {mine.current_period_end && (
                    <p className="text-xs mt-1">
                      {mine.cancel_at_period_end ? "Ends" : "Renews"} on {new Date(mine.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  data-testid="open-studio-cta"
                  onClick={() => nav("/open-studio")}
                  className="w-full px-8 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold transition-all"
                >
                  Book your free Open Studio visit →
                </button>
                {!mine.cancel_at_period_end && (
                  <button
                    data-testid="cancel-membership-btn"
                    onClick={cancel} disabled={busy}
                    className="w-full px-8 py-3 border-2 border-[#F2E9D8] text-[#F2E9D8] hover:bg-[#F2E9D8]/10 text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40 transition-all"
                  >
                    {busy ? "Working…" : "Cancel membership"}
                  </button>
                )}
              </div>
            ) : (
              <button
                data-testid="subscribe-btn"
                onClick={subscribe} disabled={busy}
                className="mt-8 w-full px-8 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center justify-center gap-3 transition-all"
              >
                {busy ? <><Loader2 size={16} className="animate-spin" /> Loading…</> : "Join now"}
              </button>
            )}
            {!mine?.active && (
              <div className="mt-6 pt-6 border-t border-[#F2E9D8]/20" data-testid="membership-code-box">
                <div className="flex items-center gap-2 text-[#C6FF33]">
                  <Ticket size={16} />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold">Have a membership code?</p>
                </div>
                <p className="text-xs text-[#F2E9D8]/60 mt-1">Redeem a free-membership code — no card required.</p>
                <div className="mt-3 flex gap-2">
                  <input
                    data-testid="membership-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && doRedeem(code)}
                    placeholder="ENTER CODE"
                    className="flex-1 min-w-0 px-3 py-2.5 bg-[#F2E9D8]/10 border border-[#F2E9D8]/30 text-[#F2E9D8] placeholder:text-[#F2E9D8]/40 focus:outline-none focus:border-[#C6FF33] tracking-widest text-sm"
                  />
                  <button
                    data-testid="membership-code-apply"
                    onClick={() => doRedeem(code)} disabled={codeBusy}
                    className="px-5 py-2.5 bg-[#F2E9D8] text-[#121212] hover:bg-white disabled:opacity-40 text-xs tracking-[0.2em] uppercase font-bold whitespace-nowrap"
                  >
                    {codeBusy ? "…" : "Redeem"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Included with Membership — value section */}
        <div className="mt-24" data-testid="membership-value">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">More than a discount — it's a creative club</p>
            <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212] leading-[0.95]">
              Included with <span className="lime-swipe italic">Membership.</span>
            </h2>
            <p className="mt-6 text-[#121212]/75 text-lg">
              Membership is your standing invitation to create, learn, and belong — here's everything you get.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUE_ITEMS.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} data-testid={`value-${i}`} className="bg-white border-2 border-[#121212] p-7 flex flex-col">
                  <div className="w-14 h-14 bg-[#C6FF33] border-2 border-[#121212] flex items-center justify-center">
                    <Icon size={26} className="text-[#121212]" />
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-3">
                    <h3 className="font-serif font-black text-xl text-[#121212] leading-tight">{v.title}</h3>
                    {v.note && <span className="shrink-0 bg-[#E0218A] text-white text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1">{v.note}</span>}
                  </div>
                  <p className="mt-3 text-[#121212]/70 text-sm leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
            {/* Highlighted statement card */}
            <div className="bg-[#121212] text-[#F2E9D8] p-7 flex flex-col justify-center items-start">
              <Star className="text-[#C6FF33]" size={28} fill="#C6FF33" />
              <p className="mt-4 font-serif font-black text-3xl leading-[1.05]">
                Everything included for only <span className="text-[#C6FF33]">$29/month.</span>
              </p>
              {!mine?.active && (
                <button
                  data-testid="value-join-btn"
                  onClick={subscribe} disabled={busy}
                  className="mt-6 px-7 py-3 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all"
                >
                  {busy ? "Loading…" : "Join the club"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
